import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import api from '../lib/api';
import { tryHandleLimitExceededFromApiError } from '../hooks/useLimitExceeded';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useAuthStore } from '../stores/auth.store';
import {
  CreateInviteDto,
  InviteDto,
  MemberDeactivationBlockedDto,
} from '@prometeus/contracts';
import { Breadcrumb } from '../components/Breadcrumb';
import { OwnerTransferModal } from '../components/OwnerTransferModal';
import { MemberRedistributeDeactivateModal } from '../components/MemberRedistributeDeactivateModal';

export const Route = createFileRoute('/w/$workspaceId/settings/members')({
  component: MembersPage,
});

type TabType = 'members' | 'invites';

function MembersPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('members');

  const canRead = hasPermission('members.read');

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver membros');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId]);

  // Members state
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [blockedDeactivation, setBlockedDeactivation] = useState<{
    memberId: string;
    details: MemberDeactivationBlockedDto;
  } | null>(null);

  // Invites state
  const [showCreateInviteModal, setShowCreateInviteModal] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteRoleId, setNewInviteRoleId] = useState<string>('');
  const [copyInviteLinkId, setCopyInviteLinkId] = useState<string | null>(null);

  // Owner transfer state
  const [showOwnerTransferModal, setShowOwnerTransferModal] = useState(false);

  const { data: members, isLoading: isLoadingMembers } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const response = await api.get('/members');
      return response.data.data;
    },
    enabled: canRead,
  });

  const { data: invites, isLoading: isLoadingInvites } = useQuery<InviteDto[]>({
    queryKey: ['invites', workspaceId],
    queryFn: async () => {
      const response = await api.get('/invites');
      return response.data.data;
    },
    enabled: canRead && activeTab === 'invites',
  });

  const { data: roles } = useQuery({
    queryKey: ['roles', workspaceId],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data.data;
    },
    enabled: canRead,
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ memberId, roleId }: { memberId: string; roleId: string | null }) => {
      // Normalize: convert empty string to null, but keep valid roleId strings
      const normalizedRoleId = roleId === '' || roleId === undefined ? null : roleId;
      const response = await api.put(`/members/${memberId}/role`, { roleId: normalizedRoleId });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
      setEditingMember(null);
      setSelectedRole('');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ memberId, status }: { memberId: string; status: 'active' | 'inactive' | 'pending' }) => {
      const response = await api.put(`/members/${memberId}/status`, { status });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
      setDeleteConfirmId(null);
      setBlockedDeactivation(null);
    },
    onError: (error: unknown, variables) => {
      const details = axios.isAxiosError(error)
        ? (error.response?.data as MemberDeactivationBlockedDto | undefined)
        : undefined;
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        details?.code === 'MEMBER_DEACTIVATION_BLOCKED'
      ) {
        setDeleteConfirmId(null);
        setBlockedDeactivation({
          memberId: variables.memberId,
          details,
        });
        return;
      }
      alert(
        (axios.isAxiosError(error) && error.response?.data?.message) ||
          'Erro ao alterar status do membro'
      );
    },
  });

  const createInviteMutation = useMutation({
    mutationFn: async (dto: CreateInviteDto) => {
      const response = await api.post('/invites', dto);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites', workspaceId] });
      setShowCreateInviteModal(false);
      setNewInviteEmail('');
      setNewInviteRoleId('');
    },
    onError: (error: any) => {
      if (tryHandleLimitExceededFromApiError(error)) {
        return;
      }
      alert(error.response?.data?.message || 'Erro ao criar convite');
    },
  });

  const deactivateInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await api.put(`/invites/${inviteId}/deactivate`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites', workspaceId] });
    },
  });

  const activateInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await api.put(`/invites/${inviteId}/activate`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites', workspaceId] });
    },
  });

  const handleEditRole = (member: any) => {
    if (!requirePermission('members.update_role', 'Alterar role de membros')) return;
    setEditingMember(member.id);
    setSelectedRole(member.roleId || '');
  };

  const handleSaveRole = (memberId: string) => {
    if (!requirePermission('members.update_role', 'Alterar role de membros')) return;
    updateRoleMutation.mutate({ 
      memberId, 
      roleId: selectedRole || null 
    });
  };

  const handleDeactivate = (memberId: string) => {
    if (!requirePermission('members.deactivate', 'Desativar membros')) return;
    updateStatusMutation.mutate({ memberId, status: 'inactive' });
  };

  const handleActivateMember = (memberId: string) => {
    if (!requirePermission('members.deactivate', 'Desativar membros')) return;
    updateStatusMutation.mutate({ memberId, status: 'active' });
  };

  const handleCreateInvite = () => {
    if (!requirePermission('members.invite', 'Convidar membros')) return;
    if (!newInviteEmail?.trim()) {
      alert('Email é obrigatório');
      return;
    }
    createInviteMutation.mutate({
      email: newInviteEmail.trim(),
      roleId: newInviteRoleId || undefined,
    });
  };

  const handleCopyInviteLink = async (inviteId: string) => {
    try {
      const response = await api.get(`/invites/${inviteId}/link`);
      const link = response.data.data.link;
      await navigator.clipboard.writeText(link);
      setCopyInviteLinkId(inviteId);
      setTimeout(() => setCopyInviteLinkId(null), 2000);
    } catch (error) {
      alert('Erro ao copiar link');
    }
  };

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: canRead,
  });

  if (!canRead) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Configurações', to: '/w/$workspaceId/settings/members', params: { workspaceId } },
            { label: 'Membros' },
          ]}
        />
        
        <div 
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '0.25rem',
                }}
              >
                Gestão de Usuários
              </h1>
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                  fontWeight: 400,
                }}
              >
                Gerencie os membros e convites do workspace
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex justify-between items-center">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('members')}
                  className={`py-4 px-1 border-b-2 transition-colors ${
                    activeTab === 'members'
                      ? 'border-[#485A6A]'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: activeTab === 'members' ? '#485A6A' : '#718096',
                  }}
                >
                  Usuários
                </button>
                <button
                  onClick={() => setActiveTab('invites')}
                  className={`py-4 px-1 border-b-2 transition-colors ${
                    activeTab === 'invites'
                      ? 'border-[#485A6A]'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: activeTab === 'invites' ? '#485A6A' : '#718096',
                  }}
                >
                  Convites
                </button>
              </nav>

              {/* Transfer Ownership button - only show if current user is the owner and on members tab */}
              {activeTab === 'members' && members?.some((m: any) => m.userId === user?.id && m.role?.isOwner) && (
                <button
                  onClick={() => setShowOwnerTransferModal(true)}
                  className="px-4 py-2 rounded-lg transition-colors hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: '#4F46E5',
                    color: '#FFFFFF',
                  }}
                >
                  Transferir Propriedade
                </button>
              )}
            </div>
          </div>

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead 
                  style={{
                    backgroundColor: '#F9FAFB',
                  }}
                >
                  <tr>
                    <th 
                      className="px-6 py-3 text-left uppercase tracking-wider"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#718096',
                      }}
                    >
                      Nome
                    </th>
                    <th 
                      className="px-6 py-3 text-left uppercase tracking-wider"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#718096',
                      }}
                    >
                      Email
                    </th>
                    <th 
                      className="px-6 py-3 text-left uppercase tracking-wider"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#718096',
                      }}
                    >
                      Role
                    </th>
                    <th 
                      className="px-6 py-3 text-left uppercase tracking-wider"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#718096',
                      }}
                    >
                      Status
                    </th>
                    <th 
                      className="px-6 py-3 text-left uppercase tracking-wider"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#718096',
                      }}
                    >
                      Membro desde
                    </th>
                    <th 
                      className="px-6 py-3 text-left uppercase tracking-wider"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#718096',
                      }}
                    >
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {members?.map((member: any) => {
                  const isOwner = member.role?.isOwner;
                  const isCurrentUser = member.userId === user?.id;

                  return (
                    <tr key={member.id}>
                      <td 
                        className="px-6 py-4 whitespace-nowrap"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#2D3748',
                        }}
                      >
                        {member.user.name} {isCurrentUser && '(Você)'}
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {member.user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingMember === member.id && !isOwner ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedRole}
                              onChange={(e) => setSelectedRole(e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded-[10px]"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              <option value="">Sem role</option>
                              {roles?.map((role: any) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleSaveRole(member.id)}
                              disabled={updateRoleMutation.isPending}
                              className="px-3 py-1.5 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                backgroundColor: '#03131B',
                                color: '#FFFFFF',
                              }}
                            >
                              Salvar
                            </button>
                            <button
                              onClick={() => {
                                setEditingMember(null);
                                setSelectedRole('');
                              }}
                              className="px-3 py-1.5 rounded-[10px] transition-colors hover:opacity-80"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                backgroundColor: '#F9FAFB',
                                color: '#2D3748',
                              }}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <span
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          >
                            {member.role?.name || 'Sem role'}
                            {isOwner && (
                              <span 
                                className="ml-2 px-2 py-1 text-xs rounded"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  backgroundColor: '#E9D5FF',
                                  color: '#6B21A8',
                                }}
                              >
                                OWNER
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            member.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : member.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {member.status === 'active' ? 'Ativo' : member.status === 'pending' ? 'Pendente' : 'Inativo'}
                        </span>
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {new Date(member.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {editingMember !== member.id && !isOwner && (
                            <button
                              onClick={() => handleEditRole(member)}
                              className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                backgroundColor: '#F9FAFB',
                                color: '#2D3748',
                              }}
                            >
                              Editar Role
                            </button>
                          )}
                          {isOwner && (
                            <span 
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '10px',
                                color: '#A0AEC0',
                              }}
                            >
                              Role não pode ser alterada
                            </span>
                          )}

                          {member.status === 'active' && editingMember !== member.id && !isOwner && (
                            <button
                              onClick={() => {
                                if (!requirePermission('members.deactivate', 'Desativar membros')) return;
                                setDeleteConfirmId(member.id);
                              }}
                              className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                backgroundColor: '#F9FAFB',
                                color: '#EF4444',
                              }}
                            >
                              Desativar
                            </button>
                          )}
                          {isOwner && member.status === 'active' && (
                            <span 
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '10px',
                                color: '#A0AEC0',
                              }}
                            >
                              Não pode ser desativado
                            </span>
                          )}

                          {member.status === 'inactive' && editingMember !== member.id && (
                            <button
                              onClick={() => handleActivateMember(member.id)}
                              className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                backgroundColor: '#F9FAFB',
                                color: '#10B981',
                              }}
                            >
                              Ativar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

          {/* Invites Tab */}
          {activeTab === 'invites' && (
            <div>
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => {
                    if (!requirePermission('members.invite', 'Convidar membros')) return;
                    setShowCreateInviteModal(true);
                  }}
                  className="rounded-lg transition-colors hover:opacity-80"
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#F9FAFB',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Novo Convite"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="#2D3748"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
              </div>

              <div className="overflow-hidden">
                {isLoadingInvites ? (
                  <div 
                    className="p-8 text-center"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                    }}
                  >
                    Carregando...
                  </div>
                ) : invites?.length === 0 ? (
                  <div 
                    className="p-8 text-center"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                    }}
                  >
                    Nenhum convite encontrado.
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead 
                      style={{
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <tr>
                        <th 
                          className="px-6 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Email
                        </th>
                        <th 
                          className="px-6 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Nome
                        </th>
                        <th 
                          className="px-6 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Role
                        </th>
                        <th 
                          className="px-6 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Status
                        </th>
                        <th 
                          className="px-6 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Criado em
                        </th>
                        <th 
                          className="px-6 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invites?.map((invite) => (
                        <tr key={invite.id}>
                          <td 
                            className="px-6 py-4 whitespace-nowrap"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          >
                            {invite.email}
                          </td>
                          <td 
                            className="px-6 py-4 whitespace-nowrap"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {invite.name || '—'}
                          </td>
                          <td 
                            className="px-6 py-4 whitespace-nowrap"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {invite.role?.name || 'Sem role'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                invite.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : invite.status === 'accepted'
                                  ? 'bg-green-100 text-green-800'
                                  : invite.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : invite.status === 'expired'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              {invite.status === 'pending'
                                ? 'Pendente'
                                : invite.status === 'accepted'
                                ? 'Aceito'
                                : invite.status === 'rejected'
                                ? 'Rejeitado'
                                : invite.status === 'expired'
                                ? 'Expirado'
                                : invite.status === 'deactivated'
                                ? 'Desativado'
                                : invite.status}
                            </span>
                          </td>
                          <td 
                            className="px-6 py-4 whitespace-nowrap"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {new Date(invite.createdAt).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              {invite.existingUser ? (
                                <span
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '11px',
                                    color: '#718096',
                                  }}
                                >
                                  Usuário já possui conta — pode aceitar em Meus Convites
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleCopyInviteLink(invite.id)}
                                  className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    backgroundColor: '#F9FAFB',
                                    color: '#2D3748',
                                  }}
                                >
                                  {copyInviteLinkId === invite.id ? 'Copiado!' : 'Copiar Link'}
                                </button>
                              )}
                              {invite.status === 'pending' && (
                                <button
                                  onClick={() => {
                                    if (!requirePermission('members.invite', 'Convidar membros')) return;
                                    deactivateInviteMutation.mutate(invite.id);
                                  }}
                                  className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    backgroundColor: '#F9FAFB',
                                    color: '#EF4444',
                                  }}
                                >
                                  Desativar
                                </button>
                              )}
                              {invite.status === 'deactivated' && (
                                <button
                                  onClick={() => {
                                    if (!requirePermission('members.invite', 'Convidar membros')) return;
                                    activateInviteMutation.mutate(invite.id);
                                  }}
                                  className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    backgroundColor: '#F9FAFB',
                                    color: '#10B981',
                                  }}
                                >
                                  Ativar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Create Invite Modal - portal para ficar acima da sidebar e header */}
        {showCreateInviteModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center p-4" style={{ zIndex: 9998 }} onClick={() => { setShowCreateInviteModal(false); setNewInviteEmail(''); setNewInviteRoleId(''); }}>
            <div
              className="bg-white p-8 w-full max-w-md relative"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                zIndex: 9999,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2D3748',
                  }}
                >
                  Novo Convite
                </h2>
                <button
                  onClick={() => {
                    setShowCreateInviteModal(false);
                    setNewInviteEmail('');
                    setNewInviteRoleId('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Email <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={newInviteEmail}
                    onChange={(e) => setNewInviteEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Role
                  </label>
                  <select
                    value={newInviteRoleId}
                    onChange={(e) => setNewInviteRoleId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                  >
                    <option value="">Sem role</option>
                    {roles?.filter((role: any) => !role.isOwner).map((role: any) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCreateInvite}
                    disabled={createInviteMutation.isPending}
                    className="flex-1 px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      backgroundColor: '#03131B',
                      color: '#FFFFFF',
                    }}
                  >
                    {createInviteMutation.isPending ? 'Criando...' : 'Criar Convite'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateInviteModal(false);
                      setNewInviteEmail('');
                      setNewInviteRoleId('');
                    }}
                    type="button"
                    className="flex-1 px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      backgroundColor: '#F9FAFB',
                      color: '#2D3748',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete/Deactivate Confirmation Modal - portal para ficar acima da sidebar e header */}
        {deleteConfirmId && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center p-4" style={{ zIndex: 9998 }} onClick={() => setDeleteConfirmId(null)}>
            <div
              className="bg-white p-8 max-w-md w-full relative"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                zIndex: 9999,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '1rem',
                }}
              >
                Confirmar Desativação
              </h2>
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#718096',
                  marginBottom: '1.5rem',
                }}
              >
                Tem certeza que deseja desativar este membro? O membro perderá acesso ao workspace.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!deleteConfirmId) return;
                    handleDeactivate(deleteConfirmId);
                  }}
                  disabled={updateStatusMutation.isPending}
                  className="flex-1 px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#EF4444',
                    color: '#FFFFFF',
                  }}
                >
                  {updateStatusMutation.isPending ? 'Desativando...' : 'Desativar'}
                </button>
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#F9FAFB',
                    color: '#2D3748',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Owner Transfer Modal */}
        <OwnerTransferModal
          isOpen={showOwnerTransferModal}
          onClose={() => setShowOwnerTransferModal(false)}
          members={members || []}
          currentOwner={members?.find((m: any) => m.userId === user?.id && m.role?.isOwner)}
        />
        <MemberRedistributeDeactivateModal
          blocked={blockedDeactivation?.details || null}
          memberId={blockedDeactivation?.memberId || null}
          members={members || []}
          onClose={() => setBlockedDeactivation(null)}
          onConfirm={() => {
            if (!blockedDeactivation) return Promise.resolve();
            return updateStatusMutation.mutateAsync({
              memberId: blockedDeactivation.memberId,
              status: 'inactive',
            });
          }}
        />
      </div>
    </div>
  );
}
